import {
  Autocomplete,
  Badge,
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  debounce,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import PlaceIcon from '@mui/icons-material/Place';
import EvStationIcon from '@mui/icons-material/EvStation';
import { SyntheticEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ocppService } from '../../../services/ocpp/OcppService';
import { StationsLocationsTypes } from './types';

export interface MapSearchOption {
  kind: 'station' | 'address';
  label: string;
  secondary?: string;
  station?: StationsLocationsTypes;
  lat?: number;
  lng?: number;
  placeId?: string;
  code?: number;
}

interface MapSearchBarProps {
  activeFiltersCount: number;
  onSelectStation: (station: StationsLocationsTypes) => void;
  onSelectPlace: (lat: number, lng: number) => void;
  onOpenFilters: () => void;
}

const HEIGHT = 48;

export const MapSearchBar = ({
  activeFiltersCount,
  onSelectStation,
  onSelectPlace,
  onOpenFilters,
}: MapSearchBarProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<MapSearchOption[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchOptions = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query || query.length < 2) {
          setOptions([]);
          setLoading(false);
          return;
        }
        setLoading(true);
        try {
          const stations = await ocppService.getStationsLocationsBySearch({
            address: query,
          });
          const stationOpts: MapSearchOption[] = stations
            .filter((s) => s.latitude && s.longitude)
            .slice(0, 8)
            .map((s) => ({
              kind: 'station',
              label: s.address,
              secondary: s.code ? `#${s.code}` : undefined,
              station: s,
              lat: s.latitude,
              lng: s.longitude,
              code: s.code,
            }));

          let placeOpts: MapSearchOption[] = [];
          if (
            typeof google !== 'undefined' &&
            google.maps?.places?.AutocompleteService
          ) {
            placeOpts = await new Promise<MapSearchOption[]>((resolve) => {
              const svc = new google.maps.places.AutocompleteService();
              svc.getPlacePredictions(
                { input: query },
                (results) => {
                  if (!results) {
                    resolve([]);
                    return;
                  }
                  resolve(
                    results.slice(0, 5).map((r) => ({
                      kind: 'address',
                      label: r.structured_formatting.main_text,
                      secondary: r.structured_formatting.secondary_text,
                      placeId: r.place_id,
                    })),
                  );
                },
              );
            });
          }

          setOptions([...stationOpts, ...placeOpts]);
        } catch {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 300),
    [],
  );

  const handleInputChange = (_e: SyntheticEvent, value: string) => {
    setInputValue(value);
    fetchOptions(value);
  };

  const handleChange = (_e: SyntheticEvent, value: MapSearchOption | string | null) => {
    if (!value || typeof value === 'string') return;
    if (value.kind === 'station' && value.station) {
      onSelectStation(value.station);
    } else if (value.kind === 'address' && value.placeId) {
      const placesService = new google.maps.places.PlacesService(
        document.createElement('div'),
      );
      placesService.getDetails(
        { placeId: value.placeId, fields: ['geometry'] },
        (place) => {
          if (place?.geometry?.location) {
            onSelectPlace(
              place.geometry.location.lat(),
              place.geometry.location.lng(),
            );
          }
        },
      );
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="stretch" sx={{ width: '100%' }}>
      <Autocomplete<MapSearchOption, false, false, true>
        freeSolo
        fullWidth
        options={options}
        loading={loading}
        loadingText={t('stationPageWithSearch.loading')}
        noOptionsText={t('stationPageWithSearch.noOptions')}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        onChange={handleChange}
        filterOptions={(x) => x}
        groupBy={(option) =>
          option.kind === 'station'
            ? t('mapPage.stations')
            : t('mapPage.addresses')
        }
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.label
        }
        renderOption={(props, option) => (
          <Box
            component="li"
            {...props}
            key={`${option.kind}-${option.placeId ?? option.station?._id ?? option.label}`}
            sx={{
              display: 'flex !important',
              alignItems: 'center !important',
              gap: 1.25,
              py: 1.25,
              px: 2,
              width: '100%',
              borderBottom: '1px solid',
              borderColor: 'grey.800',
              '&:last-child': { borderBottom: 'none' },
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: option.kind === 'station' ? 'rgba(29,239,131,0.15)' : 'grey.800',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {option.kind === 'station' ? (
                <EvStationIcon sx={{ color: 'green.main', fontSize: 18 }} />
              ) : (
                <PlaceIcon sx={{ color: 'grey.400', fontSize: 18 }} />
              )}
            </Box>
            <Stack sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                color="common.white"
                sx={{
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {option.label}
              </Typography>
              {option.secondary && (
                <Typography
                  variant="caption"
                  color="grey.400"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {option.secondary}
                </Typography>
              )}
            </Stack>
          </Box>
        )}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: 'grey.900',
              border: '1px solid',
              borderColor: 'grey.700',
              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
              borderRadius: '12px',
              mt: 0.5,
              overflow: 'hidden',
              '& .MuiAutocomplete-listbox': { p: 0, maxHeight: '60vh' },
              '& .MuiAutocomplete-groupLabel': {
                backgroundColor: 'grey.900',
                color: 'grey.500',
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                lineHeight: '32px',
              },
              '& .MuiAutocomplete-option': {
                p: 0,
                '&:hover': { backgroundColor: 'grey.800' },
                '&[aria-selected="true"]': { backgroundColor: 'grey.800' },
                '&.Mui-focused': { backgroundColor: 'grey.800' },
              },
              '& .MuiAutocomplete-loading, & .MuiAutocomplete-noOptions': {
                color: 'grey.400',
                py: 2,
              },
            },
          },
          clearIndicator: {
            sx: {
              color: 'grey.500',
              '&:hover': { color: 'grey.300', backgroundColor: 'grey.800' },
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={t('stationPageWithSearch.searchPlaceholder')}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'grey.500' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(20,20,20,0.92)',
                backdropFilter: 'blur(12px)',
                borderRadius: '14px',
                height: HEIGHT,
                boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
                '& fieldset': { borderColor: 'grey.800', borderWidth: 1 },
                '&:hover fieldset': { borderColor: 'grey.700' },
                '&.Mui-focused': {
                  boxShadow: '0 0 0 2px rgba(29,239,131,0.15), 0 4px 16px rgba(0,0,0,0.35)',
                  '& fieldset': { borderColor: 'green.main', borderWidth: 2 },
                },
              },
              '& .MuiInputBase-input': {
                color: 'common.white',
                fontSize: 14,
                fontWeight: 400,
                '&::placeholder': { color: 'grey.500', opacity: 1 },
              },
            }}
          />
        )}
        sx={{ flex: 1 }}
      />
      <IconButton
        onClick={onOpenFilters}
        sx={{
          backgroundColor: 'rgba(20,20,20,0.92)',
          backdropFilter: 'blur(12px)',
          borderRadius: '14px',
          width: HEIGHT,
          height: HEIGHT,
          minWidth: HEIGHT,
          border: '1px solid',
          borderColor: 'grey.800',
          boxShadow: '0 4px 16px rgba(0,0,0,0.35)',
          '&:hover': {
            backgroundColor: 'rgba(30,30,30,0.92)',
            borderColor: 'grey.700',
          },
        }}
      >
        <Badge
          badgeContent={activeFiltersCount}
          color="success"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: 'green.main',
              color: 'common.white',
              fontSize: 10,
              minWidth: 16,
              height: 16,
            },
          }}
        >
          <TuneIcon sx={{ color: 'grey.400' }} />
        </Badge>
      </IconButton>
    </Stack>
  );
};
